"use client";

import { useEffect, useState } from "react";

const STORAGE_KEY = "sucom:admin:store-overrides";

export interface StoreOverride {
  name?: string;
  category?: string;
  website?: string;
  logo?: string | null;
  logoVisual?: string | null;
  logoWhite?: string | null;
  bgColor?: string | null;
  cashbackRate?: number | null;
  description?: string | null;
  kind?: "TIENDA" | "MARCA" | "BOTH";
}

export function readAllOverrides(): Record<string, StoreOverride> {
  if (typeof window === "undefined") return {};
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export function readOverrideForSlug(slug: string): StoreOverride | null {
  const all = readAllOverrides();
  return all[slug] ?? null;
}

/**
 * Hook that merges a server-fetched store object with any localStorage overrides.
 * Returns the merged store, reactively updated once the client hydrates.
 */
export function useStoreWithOverrides<T extends { slug: string }>(store: T): T {
  const [merged, setMerged] = useState<T>(store);

  useEffect(() => {
    const override = readOverrideForSlug(store.slug);
    if (override) {
      setMerged((prev) => ({ ...prev, ...override }));
    }
  }, [store.slug]);

  return merged;
}
